import type { CityConfig } from "../city-template/types";

export const loganUTConfig: CityConfig = {
  city: "Logan",
  state: "Utah",
  stateAbbr: "UT",
  slug: "logan",
  route: "/cities/logan",
  title: "Emergency Passport Logan, UT | Expedited Passport Services | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Logan, UT — as fast as 24 hours. Serving Cache County and Northern Utah. A+ BBB rated registered State Dept. courier serving Utah State University and the Cache Valley region. No office visit required.",
  h1: "Emergency & Expedited Passport Services in Logan, UT",
  heroSubtitle: "USU study abroad trip to Europe in two weeks? Cache Valley business conference in Toronto and your passport expired? Summer road trip to Canada through Idaho and your documents are not ready? We help Logan residents get their passports fast — as fast as 24 hours. A+ BBB rated registered State Dept. courier. Best price guarantee. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Aerial%20view%20of%20Logan%20Utah%20Cache%20Valley%20with%20Utah%20State%20University%20campus%20Old%20Main%20building%20Logan%20LDS%20Temple%20mountains%20in%20background%20Bear%20River%20Mountains%20Wellsville%20range%20autumn%20colors%20golden%20hour%20warm%20light%20professional%20photography%20northern%20Utah%20landscape&width=1800&height=700&seq=logan-ut-hero-2026&orientation=landscape",
  heroImageAlt: "Logan Utah Cache Valley with Utah State University campus and Bear River Mountains at golden hour",
  geoPosition: "41.7369;-111.8338",
  geoPlacename: "Logan, Utah",
  regionalAgency: {
    name: "Denver Passport Agency",
    address: "3151 S Vaughn Way, Suite 600, Aurora, CO 80014",
    distance: "Located in Aurora, CO — approximately 520 miles southeast of Logan via I-15/I-80",
  },
  nearbyCitiesZips: [
    { name: "Logan", zips: "84321, 84322, 84323, 84341" },
    { name: "North Logan", zips: "84341" },
    { name: "Smithfield", zips: "84335" },
    { name: "Hyrum", zips: "84319" },
    { name: "Providence", zips: "84332" },
    { name: "Brigham City", zips: "84302" },
    { name: "Tremonton", zips: "84337" },
    { name: "Preston", zips: "83263" },
    { name: "Richmond", zips: "84333" },
    { name: "Wellsville", zips: "84339" },
    { name: "Mendon", zips: "84325" },
    { name: "Nibley", zips: "84321" },
    { name: "River Heights", zips: "84321" },
    { name: "Millville", zips: "84326" },
    { name: "Paradise", zips: "84328" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Cache Valley departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-map-pin-2-line", title: "Cache Valley Region", desc: "Serving Logan, Smithfield, Hyrum, and all of Cache County" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Ogden, UT", route: "/cities/ogden" },
    { name: "Fast passport renewal in Salt Lake City, UT", route: "/cities/salt-lake-city" },
    { name: "Emergency passport help in Provo, UT", route: "/cities/provo" },
    { name: "Urgent passport services in St. George, UT", route: "/cities/st-george" },
    { name: "Same-day passport help in Denver, CO", route: "/cities/denver" },
    { name: "Passport courier services in Boise, ID", route: "/cities/boise" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Logan remotely via secure courier. We do not have a walk-in office in Logan — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Cache County and Northern Utah.",
  fpcOfficeAddress: "525 N Tryon St., Charlotte, NC 28202",
  fpcOfficeDistance: "Serving all of Cache County and the Cache Valley region including Box Elder and Franklin counties",
  serviceAreaIntro: "From the historic USU campus on Old Main Hill and the vibrant Main Street of Logan to the agricultural communities of Smithfield and Hyrum, from the mountain town charm of Providence to the railroad heritage of Brigham City, from the Bear River Valley communities of Tremonton to the scenic Wellsville Mountains — Fast Passport Center serves the entire Cache Valley and Northern Utah region. We regularly help travelers from Cache County, Box Elder County, and southern Idaho get their passports fast without the 520-mile drive to Denver.",
  nearbyCitiesIntro: "We serve all Logan neighborhoods and the surrounding Cache Valley area including North Logan, Smithfield, Hyrum, Providence, Brigham City, Tremonton, Wellsville, Richmond, and Nibley. If you are anywhere in Cache County or northern Box Elder County, we have you covered.",
  howItWorksIntro: "Most Logan travelers complete everything online and ship documents overnight. No need to drive to Denver or Salt Lake City — we handle the courier submission for you.",
  mistakesIntro: "Logan travelers often face an 8-hour drive to the nearest regional passport agency in Denver. Here are the most common mistakes that cause delays.",
  testimonialsIntro: "Logan and Cache Valley travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Logan? Here are the most common ones we hear from Cache Valley travelers.",
  statePageRoute: "/states/utah",
  statePageName: "Utah",
  dmvWebsite: "https://dmv.utah.gov",
  dmvLabel: "Utah DMV — Division of Motor Vehicles",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For Logan travelers with immediate departure needs — as fast as 24 hours for urgent international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
    {
      icon: "ri-bank-card-line",
      title: "Passport Card",
      description: "Wallet-sized U.S. Passport Card for land and sea travel to Canada, Mexico, Bermuda, and the Caribbean.",
      bullets: ["REAL ID compliant alternative", "Fits in your wallet", "Land/sea border crossing approved"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-card",
    },
    {
      icon: "ri-error-warning-line",
      title: "Damaged Passport",
      description: "Replacement for water-damaged, torn, or otherwise mutilated passports that are no longer valid for travel.",
      bullets: ["Damage assessment guidance", "DS-11 replacement process", "Supporting documentation help"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=damaged-passport",
    },
    {
      icon: "ri-file-copy-line",
      title: "Second Passport",
      description: "Obtain a second valid U.S. passport for frequent international travelers who need overlapping visas.",
      bullets: ["For frequent business travelers", "Overlapping visa support", "Expedited processing available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=second-passport",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
    { name: "Passport Card", description: "Wallet-sized U.S. Passport Card for land and sea travel to Canada, Mexico, Bermuda, and the Caribbean." },
    { name: "Damaged Passport", description: "Replacement for water-damaged, torn, or mutilated passports requiring DS-11 replacement." },
    { name: "Second Passport", description: "Second valid U.S. passport for frequent international travelers with overlapping visa needs." },
  ],
  testimonials: [
    {
      name: "Brenda K.",
      location: "Logan, UT",
      rating: 5,
      text: "Our son's USU study abroad in Spain was 4 days away and his passport was expired. Fast Passport Center got it in 28 hours. Never left Logan. Aggies go global!",
      service: "Passport Renewal",
    },
    {
      name: "Mark T.",
      location: "Smithfield, UT",
      rating: 5,
      text: "Business emergency in Vancouver. Fast Passport Center processed my renewal in 32 hours. My dedicated agent was incredible. Cache Valley to Canada in two days flat.",
      service: "Emergency Passport",
    },
    {
      name: "Lindsay W.",
      location: "Hyrum, UT",
      rating: 5,
      text: "My daughter needed her first passport for a school trip to London. Fast Passport Center walked us through the child passport process. Got it in 3 days. Amazing service for Cache Valley families.",
      service: "Child Passport",
    },
  ],
  facilities: [
    {
      id: "logan-ut-1",
      name: "Logan Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "75 W 200 N",
      city: "Logan",
      state: "UT",
      zip: "84321",
      phone: "(435) 752-2111",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Downtown Logan",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary Logan facility. Appointments strongly recommended. Call ahead to confirm passport acceptance hours.",
      icon: "ri-mail-line",
    },
    {
      id: "logan-ut-2",
      name: "Cache County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "179 N Main St",
      city: "Logan",
      state: "UT",
      zip: "84321",
      phone: "(435) 755-1460",
      hours: "Mon–Fri: 8:00 AM – 5:00 PM",
      distance: "Downtown Logan Historic Courthouse",
      website: "https://www.cachecounty.gov/clerk",
      websiteLabel: "Cache County Clerk",
      notes: "Accepts passport applications. Good alternative to post office. Appointment recommended.",
      icon: "ri-government-line",
    },
    {
      id: "logan-ut-3",
      name: "Brigham City Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "20 N Main St",
      city: "Brigham City",
      state: "UT",
      zip: "84302",
      phone: "(435) 723-2111",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "20 miles southwest of Logan",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Box Elder County. Full passport services available.",
      icon: "ri-mail-line",
    },
    {
      id: "logan-ut-4",
      name: "Smithfield Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "50 E 100 N",
      city: "Smithfield",
      state: "UT",
      zip: "84335",
      phone: "(435) 563-2111",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "7 miles north of Logan",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Smithfield and northern Cache Valley residents. Call ahead for passport hours.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 8 hour drive to Denver", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Logan photo shops sometimes use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Cache County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Logan applicants show up with incorrect payment and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Denver regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Cache Valley families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Logan travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Logan." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Logan address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Logan before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Logan for travelers with urgent international flights. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround — without the 8-hour drive to Denver." },
    { q: "How fast can I renew my passport in Logan?", a: "Fast Passport Center offers fast passport renewal in Logan in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value. Both options include full document review and a dedicated agent." },
    { q: "What is the closest passport agency to Logan?", a: "The nearest regional passport agency to Logan is the Denver Passport Agency, approximately 520 miles southeast via I-15/I-80. For urgent processing without the long drive, Fast Passport Center's registered courier service achieves 24–48 hour turnaround from Logan without any in-person agency visit required." },
    { q: "Can I get a same-day passport appointment near Logan?", a: "Same-day passport appointments are only available at the Denver Regional Passport Agency — approximately 8 hours from Logan — for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Cache County and Northern Utah residents." },
    { q: "Do you serve the entire Cache Valley region?", a: "Absolutely. Fast Passport Center serves the entire Cache Valley and Northern Utah region including Logan, North Logan, Smithfield, Hyrum, Providence, Brigham City, and surrounding areas. Our courier service works seamlessly from any address in Cache County or Box Elder County." },
    { q: "Can I expedite a child passport in Logan?", a: "Yes. Fast Passport Center expedites child passports in Logan for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs. We walk Cache Valley families through the entire process." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Logan with DS-64 form guidance and identity verification support. Our emergency courier service can get your replacement passport in 24–48 hours." },
    { q: "Do I need to drive to the Denver Passport Agency?", a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change." },
  ],
  photoLocations: [
    { name: "Walmart Supercenter — Logan", address: "1150 S 100 W, Logan, UT 84321", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Walgreens — Logan", address: "50 E 400 N, Logan, UT 84321", hours: "Mon–Sun: 7AM–11PM" },
    { name: "CVS Pharmacy — Logan", address: "555 E 1400 N, Logan, UT 84341", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walmart Supercenter — Brigham City", address: "1200 S Commerce Way, Brigham City, UT 84302", hours: "Mon–Sun: 6AM–11PM" },
  ],
  dmvLocations: [
    {
      name: "Utah DMV — Logan",
      address: "1400 N 1000 W, Logan, UT 84321",
      hours: "Mon–Fri: 7AM–5PM",
      note: "Driver license and REAL ID services. Appointment recommended.",
      featured: true,
    },
    {
      name: "Utah DMV — Brigham City",
      address: "1 S Main St, Brigham City, UT 84302",
      hours: "Mon–Fri: 7AM–5PM",
      note: "Serves Box Elder County. REAL ID available.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "SLC",
      name: "Salt Lake City International Airport",
      distance: "85 miles south of Logan",
      address: "776 N Terminal Dr, Salt Lake City, UT 84122",
      note: "Major international airport with direct flights worldwide. About 1 hour 20 minutes from Logan via I-15.",
      flightUrl: "https://www.slcairport.com/",
      flightLabel: "SLC Airport",
      featured: true,
    },
    {
      code: "LGU",
      name: "Logan-Cache Airport",
      distance: "3 miles northwest of downtown Logan",
      address: "2500 N 900 W, Logan, UT 84321",
      note: "Regional airport with limited commercial service. Primarily general aviation. About 10 minutes from USU campus.",
      flightUrl: "https://www.loganutah.org/government/departments/airport",
      flightLabel: "LGU Airport",
      featured: false,
    },
  ],
};